/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

export const uploadButtonVariants = cva(
  [
    'z-10 inline-flex grow items-center justify-start gap-4 wrap-break-word rounded-md text-primary cursor-pointer peer-disabled:cursor-not-allowed',
    'hover:bg-ghost-hover',
    'peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-focus peer-focus-visible:bg-ghost-hover peer-focus-visible:outline-offset-2 peer-focus-visible:border-background-200',
  ],
  {
    variants: {
      variant: {
        label: 'border border-transparent py-3 px-6 bg-background-50 w-full',
        icon: 'border border-transparent p-2 w-fit',
        'label-outline': 'border border-primary py-2 px-4 min-w-32 w-fit bg-background-50 shadow-md ',
      },
    },
    defaultVariants: {
      variant: 'label',
    },
  },
);
export type UploadButtonVariants = VariantProps<typeof uploadButtonVariants>;

@Component({
  selector: 'ods-file-upload-button',
  imports: [CommonModule],
  host: { class: 'relative' },
  template: `
    <input
      #inputElement
      type="file"
      [id]="id"
      class="peer absolute top-0 left-0 z-0 h-0 w-0 opacity-0"
      [accept]="accept"
      (click)="resetInput()"
      [disabled]="isLoading"
      [multiple]="multi"
      [attr.data-test-id]="dataTestId"
      [attr.aria-required]="isRequired"
    />
    <label [for]="id" [ngClass]="uploadButtonVariants({ variant })">
      @if (isLoading) {
        <ng-content select="[spinner]" />
      } @else {
        <ng-content select="[icon]" />
      }
      <div class="grow empty:hidden">
        <ng-content select="[text]" />
      </div>
    </label>
  `,
})
export class FileUploadButtonComponent {
  @Input({ required: true }) id!: string;
  @Input() isLoading: boolean = false;
  @Input() accept: string = '*/*';
  @Input() multi: boolean = false;
  @Input() variant: UploadButtonVariants['variant'];
  @Input({ required: true }) dataTestId: string;
  @Input() isRequired: boolean = false;

  @ViewChild('inputElement') inputElement: ElementRef = new ElementRef({});

  readonly uploadButtonVariants = uploadButtonVariants;

  resetInput(): void {
    this.inputElement.nativeElement.value = '';
  }
}
